import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, Clock, Check, AlertCircle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../lib/api';
import { formatRelativeDate } from '../lib/utils';

interface Notification {
    id: string;
    type: 'info' | 'success' | 'warning' | 'error';
    title: string;
    message: string;
    link: string | null;
    reference_type: string | null;
    reference_id: string | null;
    read: boolean;
    read_at: string | null;
    created_at: string;
}

interface NotificationsResponse {
    success: boolean;
    data: {
        notifications: Notification[];
        unreadCount: number;
    };
}

export function NotificationDropdown() {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const api = useApi();
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    // Fetch notifications
    const { data } = useQuery({
        queryKey: ['notifications'],
        queryFn: () => api.get<NotificationsResponse>('/api/notifications?limit=10'),
        refetchInterval: 60000, // Refresh every minute
    });

    const notifications = data?.data?.notifications || [];
    const unreadCount = data?.data?.unreadCount || 0;

    // Mark single notification as read
    const markReadMutation = useMutation({
        mutationFn: (id: string) => api.post(`/api/notifications/${id}/read`, {}),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });

    // Mark all as read
    const markAllReadMutation = useMutation({
        mutationFn: () => api.post('/api/notifications/read-all', {}),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });

    // Close dropdown on click outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleNotificationClick = (notification: Notification) => {
        if (!notification.read) {
            markReadMutation.mutate(notification.id);
        }
        if (notification.link) {
            navigate(notification.link);
            setIsOpen(false);
        }
    };

    const handleMarkAllRead = () => {
        markAllReadMutation.mutate();
    };

    const getIcon = (type: Notification['type']) => {
        switch (type) {
            case 'success':
                return <Check className="w-4 h-4 text-[var(--color-success)]" />;
            case 'warning':
                return <AlertCircle className="w-4 h-4 text-[var(--color-warning)]" />;
            case 'error':
                return <AlertCircle className="w-4 h-4 text-[var(--color-error)]" />;
            default:
                return <Clock className="w-4 h-4 text-[var(--color-info)]" />;
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Bell button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 rounded-lg hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                aria-label="Notifications"
            >
                <Bell className="w-5 h-5 text-[rgba(255,255,255,0.8)]" />
                {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 min-w-[18px] h-[18px] flex items-center justify-center bg-[var(--color-error)] text-white text-xs font-medium rounded-full px-1">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg shadow-lg overflow-hidden z-50 animate-slideIn">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
                        <h3 className="font-semibold text-[var(--color-text)]">Notifications</h3>
                        <div className="flex items-center gap-2">
                            {unreadCount > 0 && (
                                <button
                                    onClick={handleMarkAllRead}
                                    className="text-xs text-[var(--color-primary)] hover:underline"
                                    disabled={markAllReadMutation.isPending}
                                >
                                    {markAllReadMutation.isPending ? 'En cours...' : 'Tout marquer lu'}
                                </button>
                            )}
                            <button
                                onClick={() => setIsOpen(false)}
                                className="p-1 hover:bg-[var(--color-bg)] rounded"
                            >
                                <X className="w-4 h-4 text-[var(--color-text-muted)]" />
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="max-h-96 overflow-y-auto">
                        {notifications.length === 0 ? (
                            <div className="p-8 text-center text-[var(--color-text-muted)]">
                                Aucune notification
                            </div>
                        ) : (
                            <div>
                                {notifications.map((notification) => (
                                    <div
                                        key={notification.id}
                                        onClick={() => handleNotificationClick(notification)}
                                        className={`px-4 py-3 border-b border-[var(--color-border)] hover:bg-[var(--color-bg)] cursor-pointer transition-colors ${!notification.read ? 'bg-[var(--color-primary-light)]/30' : ''
                                            }`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="mt-0.5">{getIcon(notification.type)}</div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between gap-2">
                                                    <p className="text-sm font-medium text-[var(--color-text)] truncate">
                                                        {notification.title}
                                                    </p>
                                                    {!notification.read && (
                                                        <span className="w-2 h-2 bg-[var(--color-primary)] rounded-full flex-shrink-0" />
                                                    )}
                                                </div>
                                                <p className="text-sm text-[var(--color-text-secondary)] line-clamp-2">
                                                    {notification.message}
                                                </p>
                                                <p className="text-xs text-[var(--color-text-muted)] mt-1">
                                                    {formatRelativeDate(notification.created_at)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
